import React, { useEffect, useState, useRef, useCallback, use } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/Layout/MainLayout';
import { DownloadIcon, BackIcon, UploadIcon } from '../../../components/Dashboard/DataCenterIcons';
import '../../../assets/styles/RDEDownloadsPage.css';
import '../../../assets/styles/FDCAppendReuploadPage.css';
import commonService from '../../../services/commonService';
import Papa from "papaparse";

const CDBulkUpload: React.FC = () => {
    const navigate = useNavigate();
    const fileRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<any[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [headers, setHeaders] = useState<any[]>([]);
    const [contactCountId, setContactCountId] = useState<number | null>(null);

    const [message, setMessage] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);

    /* ---------- Fetch Headers ---------- */
    useEffect(() => {
        fetchHeaders();
    }, []);

    const fetchHeaders = async () => {
        try {
            const response = await commonService.cdget();

            let activeHeaders =
                (response?.data || [])
                    .filter((item: any) => !item.is_deleted)
                    .reverse()
                    .map((item: any) => ({
                        id: item.id,
                        header_name: item.header_name
                    }));

            const contactIndex = activeHeaders.findIndex(
                (h: any) => h.header_name === "Contact Counts"
            );

            const directDialIndex = activeHeaders.findIndex(
                (h: any) => h.header_name === "Direct Dials/Phone"
            );

            if (contactIndex !== -1 && directDialIndex !== -1) {
                const [directDialItem] = activeHeaders.splice(directDialIndex, 1);
                activeHeaders.splice(contactIndex + 1, 0, directDialItem);
            }

            // ✅ detect Contact Counts ID
            activeHeaders.forEach((h: any) => {
                if (h.header_name === "Contact Counts") {
                    setContactCountId(h.id);
                }
            });

            setHeaders(activeHeaders);

        } catch (error) {
            console.error("Failed to fetch headers");
        }
    };

    /* ---------- File Handling ---------- */

    const processFile = (file: File) => {
        setMessage(null);

        if (!file.name.toLowerCase().endsWith('.csv')) {
            setMessage({
                type: "error",
                text: "Only CSV files allowed"
            });
            return;
        }

        setSelectedFile(file);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results: Papa.ParseResult<any>) => {
                setParsedRows(results.data);
            },
        });
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            processFile(e.target.files[0]);
        }
    };

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.length) {
            processFile(e.dataTransfer.files[0]);
        }
    }, []);

    const clearFile = () => {
        setSelectedFile(null);
        setParsedRows([]);
        // setMessage(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage(null);
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    /* ---------- Submit Bulk Upload ---------- */

    const submitFile = async () => {
        setMessage(null);

        if (!selectedFile || !parsedRows.length) {
            setMessage({
                type: "error",
                text: "Please upload a valid CSV file."
            });
            return;
        }

        try {
            setIsLoading(true);

            const headerMap: Record<string, number> = {};
            headers.forEach((h: any) => {
                headerMap[h.header_name.trim().toLowerCase()] = h.id;
            });

            const payload = [];

            for (let i = 0; i < parsedRows.length; i++) {
                const row = parsedRows[i];
                const rowData: Record<number, string> = {};

                Object.keys(row).forEach((key) => {
                    const headerId = headerMap[key.trim().toLowerCase()];
                    if (headerId) {
                        rowData[headerId] = row[key]?.toString().trim() || "";
                    }
                });

                // 🔴 Validate Contact Counts
                if (
                    contactCountId &&
                    (!rowData[contactCountId] ||
                        rowData[contactCountId].trim() === "")
                ) {
                    setMessage({
                        type: "error",
                        text: `Row ${i + 1}: Contact Counts is required`
                    });
                    return;
                }

                payload.push({ row_data: rowData });
            }

            await commonService.recordCreate(payload);

            setMessage({
                type: "success",
                text: "Bulk upload successful."
            });

            clearFile();

        } catch (error: any) {
            const backendMessage =
                error?.response?.data?.message ||
                "Upload failed.";

            setMessage({
                type: "error",
                text: backendMessage
            });
        } finally {
            setIsLoading(false);
        }
    };

    /* ---------- Download Template ---------- */

    const handleDownloadTemplate = () => {
        const headerNames = headers.map((h: any) => h.header_name);

        const blob = new Blob([headerNames.join(",")], {
            type: "text/csv;charset=utf-8;",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "counts_records_template.csv";
        link.click();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <MainLayout>
            <div className="fdc-append-reupload-page" style={{ backgroundColor: '#f0f8ff' }}>

                <div className="rde-header">
                    <button onClick={() => navigate(-1)} className="rde-back-button">
                        <BackIcon /> Back
                    </button>

                    <button onClick={handleDownloadTemplate} className="rde-download-template-button">
                        <DownloadIcon /> Download template
                    </button>
                </div>

                <div className="upload-container">
                    <div className="upload-card">
                        <h4 className='mb-3'>Upload CSV File (Counts Record)</h4>

                        <div
                            className={`file-input-area ${isDragging ? 'dragging' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileRef.current?.click()}
                        >
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv"
                                hidden
                                onChange={onFileChange}
                                disabled={isLoading}
                            />

                            <div className="upload-placeholder">
                                <UploadIcon />
                                <p>{selectedFile ? selectedFile.name : 'Click to browse or drag & drop CSV'}</p>
                                <p className="upload-hint">Supports CSV files • Max 10MB</p>
                            </div>
                        </div>

                        {selectedFile && (
                            <div className="selected-file-card">
                                <div className="file-info">
                                    <span className="file-name">{selectedFile.name}</span>
                                    <span className="file-size">
                                        {formatFileSize(selectedFile.size)}
                                    </span>
                                </div>
                                <button className="remove-file-btn" onClick={clearFile}>
                                    ×
                                </button>
                            </div>
                        )}

                        {/* ✅ Message UI */}
                        {message && (
                            <div style={{
                                padding: "12px",
                                borderRadius: "8px",
                                marginBottom: "15px",
                                background:
                                    message.type === "success"
                                        ? "#e6f7ed"
                                        : "#fdecea",
                                color:
                                    message.type === "success"
                                        ? "#1e7e34"
                                        : "#c62828"
                            }}>
                                {message.text}
                            </div>
                        )}

                        <div className="action-buttons">
                            <button
                                className="btn-secondary"
                                onClick={clearFile}
                                disabled={!selectedFile || isLoading}
                            >
                                Clear
                            </button>

                            <button
                                className="btn-primary"
                                onClick={submitFile}
                                disabled={!selectedFile || isLoading}
                            >
                                {isLoading ? 'Uploading...' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default CDBulkUpload;